import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import {
  analyzeFileNormalization,
  commitFileNormalization,
  reviewFileNormalization,
} from '../fileNormalization/service';
import type {
  FileNormalizationAnalyzeResult,
} from '../fileNormalization/types';
import FileNormalizationReviewPanel from '../fileNormalization/components/FileNormalizationReviewPanel';
import {
  confirmPurchaseOrdersImport,
  createPurchaseOrdersImportFlowState,
  getPurchaseOrdersImportSummary,
  runPurchaseOrdersImportPreview,
} from '../importFlows/purchaseOrdersImportFlow';
import type {
  PurchaseOrdersImportFlowState,
} from '../importFlows/purchaseOrdersImportFlow';

import { esNumberFormat } from '../utils/numberFormat';
type IndustrialCostPurchaseOrdersSourceDialogProps = {
  open: boolean;
  file: File | null;
  onClose: () => void;
  onImported: (message: string) => void;
};

const PURCHASE_ORDERS_IMPORTER_KEY = 'purchase_orders_pending';

type ImportStep = 'structure' | 'mapping' | 'confirm';

type MappableField = {
  key: string;
  label: string;
  required?: boolean;
};

const MAPPABLE_FIELDS: MappableField[] = [
  {
    key: 'purchase_order_number',
    label: 'Pedido de compra',
    required: true,
  },
  { key: 'order_date', label: 'Fecha de emisión' },
  {
    key: 'delivery_date_fallback',
    label: 'Fecha de entrega (fallback)',
  },
  { key: 'supplier_code', label: 'Código proveedor' },
  { key: 'supplier_name', label: 'Proveedor' },
  {
    key: 'material_code',
    label: 'Artículo / material',
    required: true,
  },
  {
    key: 'material_description',
    label: 'Descripción material',
  },
  { key: 'warehouse_code', label: 'Subalmacén' },
  { key: 'quantity_delivered', label: 'Cantidad entregada' },
  {
    key: 'quantity_pending',
    label: 'Cantidad pendiente',
    required: true,
  },
  {
    key: 'expected_delivery_date',
    label: 'Fecha prevista de entrega',
    required: true,
  },
  { key: 'confirmed', label: 'Confirmada' },
  { key: 'retained', label: 'Retenido' },
  { key: 'closed', label: 'Cerrado' },
  { key: 'finished', label: 'Acabado' },
];

function getErrorMessage(error: unknown, fallback: string): string {
  if (typeof error === 'object' && error !== null && 'response' in error) {
    const detail = (
      error as { response?: { data?: { detail?: unknown } } }
    ).response?.data?.detail;

    if (typeof detail === 'string' && detail.trim()) {
      return detail;
    }
  }

  if (error instanceof Error && error.message.trim()) {
    return error.message;
  }

  return fallback;
}

function toCommitMapping(
  mapping: Record<string, string | null>,
): Record<string, string> {
  return Object.fromEntries(
    Object.entries(mapping).filter(
      (entry): entry is [string, string] =>
        typeof entry[1] === 'string' &&
        entry[1].trim().length > 0,
    ),
  );
}

export default function IndustrialCostPurchaseOrdersSourceDialog({
  open,
  file,
  onClose,
  onImported,
}: IndustrialCostPurchaseOrdersSourceDialogProps) {
  const [step, setStep] = useState<ImportStep>('structure');

  const [normalization, setNormalization] =
    useState<FileNormalizationAnalyzeResult | null>(null);

  const [selectedSheet, setSelectedSheet] = useState('');
  const [headerRowNumber, setHeaderRowNumber] = useState('1');

  const [flow, setFlow] =
    useState<PurchaseOrdersImportFlowState>(
      createPurchaseOrdersImportFlowState(file),
    );

  const [draftMapping, setDraftMapping] = useState<
    Record<string, string | null>
  >({});

  const [loading, setLoading] = useState(false);
  const [busyAction, setBusyAction] = useState(false);
  const [errorMessage, setErrorMessage] =
    useState<string | null>(null);

  const preview = flow.preview;

  const summary = useMemo(
    () => getPurchaseOrdersImportSummary(flow),
    [flow],
  );

  const canConfirm = summary.canContinue;

  const canClose =
    !loading &&
    !busyAction &&
    flow.status !== 'confirming';

  async function analyzeStructure() {
    if (!file) return;

    setLoading(true);
    setErrorMessage(null);
    setStep('structure');
    setNormalization(null);
    setFlow(createPurchaseOrdersImportFlowState(file));
    setDraftMapping({});

    try {
      const nextNormalization =
        await analyzeFileNormalization(
          file,
          PURCHASE_ORDERS_IMPORTER_KEY,
        );

      setNormalization(nextNormalization);
      setSelectedSheet(
        nextNormalization.selected_sheet ?? '',
      );
      setHeaderRowNumber(
        String(
          nextNormalization.header_row_index + 1,
        ),
      );
      setDraftMapping(
        nextNormalization.applied_column_mapping ?? {},
      );

      setFlow({
        ...createPurchaseOrdersImportFlowState(file),
        mapping:
          nextNormalization.applied_column_mapping ?? {},
        normalization: {
          sheet_name:
            nextNormalization.selected_sheet,
          header_row_index:
            nextNormalization.header_row_index,
        },
      });
    } catch (error) {
      setErrorMessage(
        getErrorMessage(
          error,
          'No se pudo analizar la estructura del archivo de pedidos de compra.',
        ),
      );
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (!open || !file) return;
    void analyzeStructure();
  }, [open, file]);

  async function handleSheetChange(
    sheetName: string,
  ) {
    if (!file || !sheetName) return;

    setBusyAction(true);
    setErrorMessage(null);

    try {
      const reviewed =
        await reviewFileNormalization(
          file,
          PURCHASE_ORDERS_IMPORTER_KEY,
          0,
          sheetName,
        );

      setNormalization(reviewed);
      setSelectedSheet(
        reviewed.selected_sheet ?? '',
      );
      setHeaderRowNumber(
        String(reviewed.header_row_index + 1),
      );

      const mapping =
        reviewed.applied_column_mapping ?? {};

      setDraftMapping(mapping);

      setFlow({
        ...createPurchaseOrdersImportFlowState(file),
        mapping,
        normalization: {
          sheet_name: reviewed.selected_sheet,
          header_row_index:
            reviewed.header_row_index,
        },
      });
    } catch (error) {
      setErrorMessage(
        getErrorMessage(
          error,
          'No se pudo cargar la muestra de la hoja seleccionada.',
        ),
      );
    } finally {
      setBusyAction(false);
    }
  }

  async function handleUseStructure() {
    if (!file || !normalization) return;

    const parsedHeaderRow =
      Number(headerRowNumber);

    if (
      !Number.isInteger(parsedHeaderRow) ||
      parsedHeaderRow < 1
    ) {
      setErrorMessage(
        'Selecciona visualmente una fila válida como cabecera.',
      );
      return;
    }

    setBusyAction(true);
    setErrorMessage(null);

    try {
      let finalNormalization =
        normalization;

      const selectedHeaderRowIndex =
        parsedHeaderRow - 1;

      const structureChanged =
        selectedSheet !==
          (normalization.selected_sheet ?? '') ||
        selectedHeaderRowIndex !==
          normalization.header_row_index;

      if (structureChanged) {
        finalNormalization =
          await reviewFileNormalization(
            file,
            PURCHASE_ORDERS_IMPORTER_KEY,
            selectedHeaderRowIndex,
            selectedSheet || null,
          );

        setNormalization(
          finalNormalization,
        );
        setSelectedSheet(
          finalNormalization.selected_sheet ?? '',
        );
        setHeaderRowNumber(
          String(
            finalNormalization.header_row_index + 1,
          ),
        );
      }

      const initialMapping =
        finalNormalization.applied_column_mapping ?? {};

      await commitFileNormalization({
        importer_key:
          PURCHASE_ORDERS_IMPORTER_KEY,
        sheet_name:
          finalNormalization.selected_sheet,
        header_row_index:
          finalNormalization.header_row_index,
        data_start_row_index:
          finalNormalization.header_row_index + 1,
        column_mapping:
          toCommitMapping(initialMapping),
        known_columns:
          finalNormalization.detected_columns,
      });

      const nextFlow =
        await runPurchaseOrdersImportPreview(
          createPurchaseOrdersImportFlowState(
            file,
          ),
          {
            file,
            mapping: initialMapping,
            normalization: {
              sheet_name:
                finalNormalization.selected_sheet,
              header_row_index:
                finalNormalization.header_row_index,
            },
          },
        );

      setFlow(nextFlow);

      if (!nextFlow.preview) {
        setErrorMessage(
          nextFlow.error ??
            'No se pudo analizar el archivo de pedidos de compra.',
        );
        return;
      }

      setDraftMapping(
        nextFlow.mapping,
      );
      setStep('mapping');
    } catch (error) {
      setErrorMessage(
        getErrorMessage(
          error,
          'No se pudo guardar la estructura seleccionada.',
        ),
      );
    } finally {
      setBusyAction(false);
    }
  }

  async function handleSaveMapping() {
    if (!file || !normalization || !preview) {
      return;
    }

    setBusyAction(true);
    setErrorMessage(null);

    try {
      const nextFlow =
        await runPurchaseOrdersImportPreview(
          flow,
          {
            file,
            mapping: draftMapping,
            normalization: {
              sheet_name:
                normalization.selected_sheet,
              header_row_index:
                normalization.header_row_index,
            },
          },
        );

      setFlow(nextFlow);

      if (!nextFlow.preview) {
        setErrorMessage(
          nextFlow.error ??
            'No se pudo validar el mapeo de pedidos de compra.',
        );
        return;
      }

      setDraftMapping(
        nextFlow.mapping,
      );

      if (
        nextFlow.preview
          .missing_required_fields.length > 0
      ) {
        setErrorMessage(
          `Faltan campos obligatorios por mapear: ${
            nextFlow.preview.missing_required_fields.join(
              ', ',
            )
          }.`,
        );
        return;
      }

      await commitFileNormalization({
        importer_key:
          PURCHASE_ORDERS_IMPORTER_KEY,
        sheet_name:
          normalization.selected_sheet,
        header_row_index:
          normalization.header_row_index,
        data_start_row_index:
          normalization.header_row_index + 1,
        column_mapping:
          toCommitMapping(nextFlow.mapping),
        known_columns:
          nextFlow.preview.columns,
      });

      setStep('confirm');
    } catch (error) {
      setErrorMessage(
        getErrorMessage(
          error,
          'No se pudo guardar el mapeo de pedidos de compra.',
        ),
      );
    } finally {
      setBusyAction(false);
    }
  }

  async function handleConfirm() {
    if (
      !file ||
      !normalization ||
      !preview ||
      !canConfirm
    ) {
      return;
    }

    setBusyAction(true);
    setErrorMessage(null);

    try {
      const nextFlow =
        await confirmPurchaseOrdersImport(
          flow,
        );

      setFlow(nextFlow);

      if (
        nextFlow.status === 'imported' ||
        nextFlow.status ===
          'imported_with_issues'
      ) {
        onImported(
          nextFlow.resultMessage ??
            'Pedidos de compra publicados.',
        );
        return;
      }

      setErrorMessage(
        nextFlow.error ??
          'No se pudo confirmar la importación de pedidos de compra.',
      );
    } finally {
      setBusyAction(false);
    }
  }

  return (
    <Dialog
      open={open}
      onClose={canClose ? onClose : undefined}
      fullWidth
      maxWidth="xl"
    >
      <DialogTitle>
        Configurar pedidos de compra pendientes
      </DialogTitle>

      <DialogContent dividers>
        <Stack spacing={2.25}>
          <Alert
            severity="info"
            variant="outlined"
          >
            Comprueba primero la hoja y selecciona
            con un clic la fila de encabezados.
            Después revisa el mapeo y confirma la
            publicación de la fotografía de compras
            pendientes.
          </Alert>

          {loading ? (
            <Alert severity="info">
              Analizando únicamente la estructura
              del archivo...
            </Alert>
          ) : null}

          {errorMessage || flow.error ? (
            <Alert severity="error">
              {errorMessage ?? flow.error}
            </Alert>
          ) : null}

          {step === 'structure' &&
          normalization ? (
            <FileNormalizationReviewPanel
              result={normalization}
              selectedSheetName={
                selectedSheet
              }
              selectedHeaderRowIndex={Math.max(
                0,
                Number(headerRowNumber) - 1,
              )}
              isReviewing={busyAction}
              isContinuing={busyAction}
              onSheetChange={(sheetName) =>
                void handleSheetChange(
                  sheetName,
                )
              }
              onHeaderRowChange={(rowIndex) =>
                setHeaderRowNumber(
                  String(rowIndex + 1),
                )
              }
              onContinue={() =>
                void handleUseStructure()
              }
            />
          ) : null}

          {step === 'mapping' &&
          preview ? (
            <Stack spacing={2}>
              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  borderRadius: 2.5,
                }}
              >
                <Stack spacing={0.6}>
                  <Typography
                    sx={{ fontWeight: 900 }}
                  >
                    Mapeo de columnas
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                  >
                    Revisa las correspondencias.
                    Los cambios se aplican juntos
                    al pulsar Guardar mapeo y
                    continuar.
                  </Typography>
                </Stack>
              </Paper>

              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: '1fr',
                    md: 'repeat(2, minmax(0, 1fr))',
                  },
                  gap: 1.25,
                }}
              >
                {MAPPABLE_FIELDS.map(
                  (field) => (
                    <TextField
                      key={field.key}
                      select
                      size="small"
                      label={`${field.label}${
                        field.required
                          ? ' *'
                          : ''
                      }`}
                      value={
                        draftMapping[
                          field.key
                        ] ?? ''
                      }
                      onChange={(event) =>
                        setDraftMapping(
                          (current) => ({
                            ...current,
                            [field.key]:
                              event.target
                                .value ||
                              null,
                          }),
                        )
                      }
                      disabled={busyAction}
                    >
                      <MenuItem value="">
                        No mapear
                      </MenuItem>
                      {preview.columns.map(
                        (column) => (
                          <MenuItem
                            key={column}
                            value={column}
                          >
                            {column}
                          </MenuItem>
                        ),
                      )}
                    </TextField>
                  ),
                )}
              </Box>

              {preview
                .missing_required_fields
                .length > 0 ? (
                <Alert
                  severity="error"
                  variant="outlined"
                >
                  Campos obligatorios sin
                  resolver:{' '}
                  {preview
                    .missing_required_fields
                    .join(', ')}
                </Alert>
              ) : null}

              <Stack
                direction="row"
                justifyContent="space-between"
              >
                <Button
                  onClick={() =>
                    setStep('structure')
                  }
                  disabled={busyAction}
                >
                  Volver
                </Button>

                <Button
                  variant="contained"
                  onClick={() =>
                    void handleSaveMapping()
                  }
                  disabled={busyAction}
                >
                  {busyAction
                    ? 'Guardando...'
                    : 'Guardar mapeo y continuar'}
                </Button>
              </Stack>
            </Stack>
          ) : null}

          {step === 'confirm' &&
          preview ? (
            <Stack spacing={2}>
              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  borderRadius: 2.5,
                }}
              >
                <Stack spacing={0.7}>
                  <Typography
                    sx={{ fontWeight: 900 }}
                  >
                    {summary.rowErrorsCount >
                    0
                      ? 'Archivo requiere revisión'
                      : 'Archivo listo para publicar'}
                  </Typography>

                  <Typography
                    variant="body2"
                    color="text.secondary"
                  >
                    Líneas pendientes:{' '}
                    {
                      preview
                        .coverage_summary
                        .rows_with_pending_quantity
                    }
                    {' · '}Material resuelto:{' '}
                    {
                      preview
                        .coverage_summary
                        .resolved_material_rows
                    }
                    {' · '}Sin resolver:{' '}
                    {
                      summary.unresolvedMaterialsCount
                    }
                    {' · '}Sin fecha:{' '}
                    {
                      summary.missingDeliveryDatesCount
                    }
                  </Typography>
                </Stack>
              </Paper>

              {summary.rowErrorsCount >
              0 ? (
                <Stack spacing={1}>
                  <Alert
                    severity="error"
                    variant="outlined"
                  >
                    Hay{' '}
                    {
                      summary.rowErrorsCount
                    }{' '}
                    línea(s) bloqueante(s).
                    La fotografía vigente se
                    mantiene y esta fuente no se
                    publicará hasta corregirlas.
                  </Alert>

                  {preview.row_errors
                    .slice(0, 20)
                    .map((row, index) => (
                      <Paper
                        key={`${String(
                          row.source_row_number ??
                            index,
                        )}-${index}`}
                        variant="outlined"
                        sx={{
                          p: 1.5,
                          borderRadius: 2,
                        }}
                      >
                        <Stack spacing={0.35}>
                          <Typography
                            sx={{
                              fontWeight: 900,
                            }}
                          >
                            Fila{' '}
                            {String(
                              row.source_row_number ??
                                '—',
                            )}
                          </Typography>

                          <Typography
                            variant="body2"
                          >
                            Pedido:{' '}
                            <strong>
                              {String(
                                row.purchase_order_number ??
                                  'vacío',
                              )}
                            </strong>
                            {' · '}Artículo:{' '}
                            <strong>
                              {String(
                                row.material_code ??
                                  'vacío',
                              )}
                            </strong>
                          </Typography>

                          <Typography
                            variant="caption"
                            color="text.secondary"
                          >
                            {String(
                              row.message ??
                                'La línea no puede incorporarse al snapshot.',
                            )}
                          </Typography>
                        </Stack>
                      </Paper>
                    ))}

                  {summary.rowErrorsCount >
                  20 ? (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                    >
                      Se muestran las primeras
                      20 incidencias de{' '}
                      {
                        summary.rowErrorsCount
                      }
                      .
                    </Typography>
                  ) : null}
                </Stack>
              ) : null}

              {preview.row_warnings.map(
                (warning, index) => (
                  <Alert
                    key={`${String(
                      warning.type ??
                        'warning',
                    )}-${index}`}
                    severity="warning"
                    variant="outlined"
                  >
                    {String(
                      warning.message ??
                        'Aviso de importación',
                    )}
                  </Alert>
                ),
              )}

              <Stack spacing={0.5}>
                {preview.preview_rows
                  .slice(0, 5)
                  .map((row, index) => (
                    <Typography
                      key={index}
                      variant="body2"
                    >
                      {String(
                        row.purchase_order_number ??
                          '—',
                      )}
                      {' · '}
                      {String(
                        row.material_code ??
                          '—',
                      )}
                      {' · '}
                      {esNumberFormat({
                          maximumFractionDigits: 2,
                        },
                      ).format(
                        Number(
                          row.quantity_pending ??
                            0,
                        ),
                      )}
                      {' · '}
                      {String(
                        row.expected_delivery_date ??
                          'sin fecha',
                      )}
                    </Typography>
                  ))}
              </Stack>

              <Stack
                direction="row"
                justifyContent="flex-start"
              >
                <Button
                  onClick={() =>
                    setStep('mapping')
                  }
                  disabled={busyAction}
                >
                  Volver al mapeo
                </Button>
              </Stack>
            </Stack>
          ) : null}
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button
          onClick={onClose}
          disabled={!canClose}
        >
          Cancelar
        </Button>

        {step === 'confirm' ? (
          <Button
            variant="contained"
            color="success"
            onClick={() =>
              void handleConfirm()
            }
            disabled={
              busyAction ||
              !canConfirm
            }
          >
            {busyAction
              ? 'Publicando...'
              : 'Confirmar carga'}
          </Button>
        ) : null}
      </DialogActions>
    </Dialog>
  );
}
